import { jwtTokenService } from '@mathflat/domain/@entities/Auth/jwtTokenService'
import { bindI18nBrand } from '@mathflat/shared/i18n/i18n'
import { addWeeks } from 'date-fns'
import { runInAction } from 'mobx'
import qs from 'qs'
import { matchPath } from 'react-router'

import designTemplateService from './contentComponents/common/WorksheetDesign/DesignTemplate.service'
import { academyController } from './controller/academy.controller'
import { contractController } from './controller/contract.controller'
import { guestController } from './controller/guest.controller'
import { teacherController } from './controller/teacher.controller'
import { userController } from './controller/user.controller'
import { UserPermission } from './models/(Permission)/UserPermission'
import { updatedContentsService } from './page/etc/UpdatedContents/UpdatedContents.service'
import kmmRegisterService from './page/Lesson-prepare/PageKMM/register/_services/KMMRegister.service'
import { podMallCartService } from './page/PODMall/pages/PODMallCart/_service/PODMallCart.service'
import { podMallUserService } from './page/PODMall/pages/PODMallManage/PODMallUser/_service/PODMallUser.service'
import commonRepo from './repository/common.repo'
import {
  AUTH_ROUTES,
  type AuthRoute,
  getEntrancePage,
  NOT_ALLOWED_TO_REDIRECT_TO_LOGIN_PATH,
  PUBLIC_ROUTES,
  RouteName,
} from './router'
import { authService } from './service/auth.service'
import { curriculumCoreService } from './service/curriculumCore.service'
import { PaymentService } from './service/Payment.service'
import { localStorageService } from './service/storage.service'
import type { AppDeviceInfo } from './service/webview.service'
import WebviewService from './service/webview.service'
import { remoteStorageService } from './utils/axios/remoteStorage'
import { initAxiosDefaultBaseURL } from './utils/axios/util'
import dateUtils from './utils/DateUtils'
import { envUtils } from './utils/envUtils'
import { buildLoginRedirectSearchParams } from './utils/loginRedirect'

type CRMLoginQSType = {
  accessToken?: string
  refreshToken?: string
  userId?: string
  academyId?: string
}

export type AfterLoginRedirectPathType = {
  redirectPathAfterLogin?: string
}

type SearchType = Partial<AppDeviceInfo> & CRMLoginQSType & AfterLoginRedirectPathType

export const checkAcademyInfoUpdatePeriod = (
  targetDate: Date,
  eventStartDate: Date,
  eventEndDate: Date = addWeeks(eventStartDate, 4),
) => {
  // TODO: 학원정보 업데이트 이벤트 기간이 아직 정해지지 않아서 시작일을 우선 임의 값으로 설정
  // - 차후 기간 업데이트 필요
  // - 로컬스토리지 값으로 테스트 가능하도록 구현

  const isEventPeriod = dateUtils.checkIsWithinInterval(new Date(), {
    startDate: eventStartDate,
    endDate: eventEndDate,
  })

  return {
    isEventPeriod,
    isUpdatedWithInEventPeriod:
      isEventPeriod &&
      dateUtils.checkIsWithinInterval(targetDate, {
        startDate: eventStartDate,
        endDate: eventEndDate,
      }),
  }
}

export const isPublicRoute = (pathname: string) => {
  return PUBLIC_ROUTES.some((publicRoute) =>
    matchPath(pathname, {
      path: publicRoute,
    }),
  )
}

const isNotAllowedToRedirectToLoginPath = (pathname: string) => {
  return NOT_ALLOWED_TO_REDIRECT_TO_LOGIN_PATH.some((route) =>
    matchPath(pathname, {
      path: route,
      // 목록에 root('/')가 포함되어 있어 exact 없이는 모든 경로가 매치되고,
      // 그 결과 redirectPathAfterLogin 이 어떤 경로에서도 붙지 않게 된다.
      exact: true,
    }),
  )
}

/**
 * 로그인 페이지로 리다이렉트
 * 특수 로그인 페이지(부산, 전남, 공공) 경로를 유지하고, 현재 경로를 redirectPathAfterLogin으로 설정
 */
const redirectToLoginPath = () => {
  const { pathname } = window.location

  // 현재 경로가 이미 로그인 페이지인지 체크
  const isLoginPathname =
    AUTH_ROUTES.includes(pathname as AuthRoute) || pathname === RouteName.serverMaintenance

  // 이미 로그인 페이지면 리다이렉트하지 않음
  if (isLoginPathname) {
    return
  }

  const loginPath = localStorageService.getItem('LOGIN_PATH') || '/login'

  // 현재 search 는 redirectPathAfterLogin 안에 통째로 보존되므로
  // 로그인 URL 에 중복으로 싣지 않는다 (웹뷰 식별 파라미터 version/device 만 예외적으로 최상위 보존)
  const searchParams = isNotAllowedToRedirectToLoginPath(pathname)
    ? new URLSearchParams(window.location.search)
    : buildLoginRedirectSearchParams(pathname, window.location.search)

  commonRepo.router.replace(`${loginPath}?${searchParams.toString()}`)
}

const authDaemon = async () => {
  const { search, pathname } = window.location

  const { accessToken, refreshToken, userId, academyId, redirectPathAfterLogin, ...webviewParams } =
    qs.parse(search, {
      ignoreQueryPrefix: true,
    }) as unknown as SearchType

  // CRM에서 로그인한 케이스
  if (accessToken && refreshToken && userId && academyId) {
    jwtTokenService.clearTokens()
    jwtTokenService.setTokens({ accessToken, refreshToken })
    authService.saveLoginResponseToStorage({
      userId,
      academyId,
    })

    const url = new URL(window.location.href)
    url.searchParams.delete('accessToken')
    url.searchParams.delete('refreshToken')
    url.searchParams.delete('userId')
    url.searchParams.delete('academyId')
    history.replaceState(null, '', url)
  }

  jwtTokenService.setEventHandlers({
    onReissueSuccess: ({ restrictions }) => {
      commonRepo.permission?.updateRestrictions(restrictions ?? [])
    },
  })

  // JWT 인증 초기화 (레거시 토큰 마이그레이션 포함)
  try {
    await authService.initializeJwtAuth()
  } catch (error) {
    // 마이그레이션 실패 시 스토리지 클리어 후 로그인 페이지로 리다이렉트
    jwtTokenService.clearTokens()
    sessionStorage.clear()
    localStorage.clear()
    redirectToLoginPath()
    return
  }

  // TODO: axios.default.baseURL 세팅이 화면단에서 이뤄져서 authDaemon보다 늦어져서
  // authDaemon 내 api가 세팅되었어야할 baseURL과 달라짐.
  // 일단 화면단의 로직을 공통함수로 빼서 authDaemon 상단에서 먼저 실행하는 식으로 고쳤지만,
  // 더 좋은 방법이 있으면 수정할 것.
  if (envUtils.isDev) {
    initAxiosDefaultBaseURL(localStorageService.getItem('DEV_API') ?? 'dev')
  }

  // 웹뷰 설정
  if (
    WebviewService.isAppAndroid &&
    webviewParams &&
    webviewParams.device &&
    webviewParams.version
  ) {
    WebviewService.device = webviewParams.device
    WebviewService.version = parseInt(webviewParams.version)
  }

  if (isPublicRoute(pathname)) return

  // 임시 - 차후 localstorageService로 교체
  const teacherId = localStorage.getItem('teacher-id')
  if (teacherId) {
    localStorageService.setItem('TEACHER_ID')(teacherId)
  }

  // 로그인 되어 있다면
  if (jwtTokenService.isLoggedIn && teacherId) {
    remoteStorageService.initRelationId(teacherId)
    remoteStorageService.get('visited유형분석')
    remoteStorageService.get('visitedWorksheetBoardView')
    remoteStorageService.get('visitedWorkbookBoardView')
    remoteStorageService.get('learningStatisticsSortedType')
    remoteStorageService.get('ONE_CLICK_WORKSHEET_OPTIONS')
    remoteStorageService.get('ESSAY_WORKSHEET_OPEN_NOTIFICATION')
    remoteStorageService.get('PUBLIC_WORKBOOK_OPEN_NOTIFICATION')
    await remoteStorageService.get('PREVIOUS_SELECTED_CURRICULUM_INFO_유형분석')
    if (remoteStorageService.PREVIOUS_SELECTED_CURRICULUM_INFO_유형분석) {
      await remoteStorageService.migrateAchievementAnalysisCurriculumInfoData()
    }

    try {
      designTemplateService.fetchAllDesignTemplateList()
      podMallCartService.fetchSetCartItems()
      podMallUserService.fetchSetUser()
      const [user, academy, b2gContractInfo, me] = await Promise.all([
        teacherController.fetchUserTeacher(teacherId),
        academyController.fetchMyAcademy(),
        contractController.fetchContractInfo(),
        userController.fetchMe(),
        kmmRegisterService.initialize(),
      ])

      localStorageService.setItem('ACADEMY_ID')(user.academyId)

      runInAction(() => {
        commonRepo.user = user
        commonRepo.academy = academy
        commonRepo.payment = new PaymentService()
        commonRepo.permission = new UserPermission({
          userAuthority: user.type,
          academyFeatureAuthority: academy.authority,
          subscribedCurriculums: academy.subscribeItems,
          payStatus: academy.payStatus,
          isSchoolflat:
            envUtils.isSchool || !!(academy.customerType && academy.customerType.includes('B2G')),
          isSeoltab: academy.academyAdditionalData.isSeoltab,
          isFranchise: academy.isFranchise,
          restrictions: me.restrictions ?? [],
        })
        commonRepo.b2gContract = b2gContractInfo
      })

      // prefetchQuery 는 실패를 내부에서 삼킨다(로그인 흐름과 무관) — 실제 사용 시 fetchGuests 가 재시도한다.
      if (commonRepo.permission.byFeature.isAllowed('CREVERSE_GUEST')) {
        void guestController.prefetchGuests()
      }

      await curriculumCoreService.fetchSetCurriculum({
        plan: commonRepo.isSolo ? 'solo' : 'default',
        subscribedCurriculums: commonRepo.permission.bySubscription.솔로_구독_커리큘럼,
      })

      // 로그인 페이지로 첫 진입한 경우 부팅 시점엔 academy 가 없어 default 번들만 깔려 있다.
      // 로그인 완료 후 받아온 academy 의 brand 번들을 주입하고 remote 언어팩을 시도한다.
      // router.push 직전에 await 해, 다음 페이지가 default 로 mount 되어 brand 문구가 늦게 반영되는 race 를 막는다.
      // 비핵심 리소스라 실패가 바깥 catch(토큰 클리어/로그아웃)로 전파되지 않게 격리한다.
      try {
        await bindI18nBrand(
          { academyGroupType: academy.academyGroupType, customerType: academy.customerType },
          academy.languagePackFileUrl,
        )
      } catch (error) {
        console.warn('bindI18nBrand 실패, default 번들로 진행합니다.', error)
      }

      const loginPath = localStorageService.getItem('LOGIN_PATH') || '/login'
      if (!commonRepo.academy?.initialized) {
        // 로그인시, 초기 셋팅이 안된 유저는 이닛셋팅 페이지로 이동
        commonRepo.router.push(RouteName.initSetting)
      } else if (pathname === loginPath || pathname === '/') {
        // 우선순위 1) redirectPathAfterLogin 있을 경우
        if (redirectPathAfterLogin) {
          const redirectParams = new URL(redirectPathAfterLogin, window.location.origin)
            .searchParams
          const expectedLoginId = redirectParams.get('expectedLoginId')

          if (!expectedLoginId) {
            commonRepo.router.push(redirectPathAfterLogin)
            return
          }

          // expectedLoginId가 있으면 현재 로그인 계정과 일치할 때만 동의 페이지로 이동
          const savedLoginId = String(localStorageService.getItem('SAVED_LOGIN_ID') ?? '')
          if (savedLoginId && savedLoginId === expectedLoginId) {
            commonRepo.router.push(redirectPathAfterLogin)
            return
          }
          // 계정 불일치 시 redirectPathAfterLogin 유지한 채 재로그인 유도
          jwtTokenService.clearTokens(false)
          window.location.replace(
            `${loginPath}?redirectPathAfterLogin=${encodeURIComponent(redirectPathAfterLogin)}`,
          )
          return
        }

        // 우선순위 2) 콘텐츠 업데이트 페이지 이동 가능할 경우
        const updatedContent = updatedContentsService.getUpdatedContent()
        if (updatedContent) {
          updatedContentsService.updateStoredInfo(updatedContent.id)
        }
        // 우선순위 3) 그 외 경우 기본 페이지로 이동
        commonRepo.router.push(
          getEntrancePage(
            { isSchoolflat: commonRepo.isSchoolflat, isSeoltab: commonRepo.isSeoltab },
            !!updatedContent,
          ),
        )
      }
    } catch (e) {
      console.error(e)
      //200이 아닐때 (토큰이 invalid하거나 기타 오류가 날 때)
      jwtTokenService.clearTokens()

      // CRM 로그인 플로우에서 토큰이 없다면 로그인 페이지로 이동
      if (accessToken || refreshToken || academyId || userId) {
        const loginPath = localStorageService.getItem('LOGIN_PATH') || '/login'
        window.location.replace(loginPath)
        return
      }

      redirectToLoginPath()
    }
  } else if (!jwtTokenService.isLoggedIn) {
    //token이 없다면
    //다른 탭이 유효한 토큰을 갖고 있는지 req날림 - 갖고있다면 토큰값 갖고 페이지 리로드 되면서 여기서 끊김
    jwtTokenService.requestToken()
  } else {
    // 토큰은 있지만 teacherId가 없는 경우 (localStorage 부분 유실)
    // requestToken()을 호출하면 다른 탭이 토큰으로 응답 → reload → teacherId 없는 동일 상태 → 무한 반복
    // 토큰을 클리어하고 로그인 페이지로 이동
    jwtTokenService.clearTokens()
    redirectToLoginPath()
  }
}

export default authDaemon
